import { Component, OnInit, Input, HostListener, ApplicationRef } from '@angular/core';
import { Location } from '@angular/common';
import { Router, NavigationEnd, RouterEvent } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PollService } from './services/poll.service';
import { EventBusService, EventType, ATMEventData } from './services/eventbus.service';
import { SystemStatusService } from './services/systemstatus.service';
import { User, AuthenticationService } from '@csx/security';
import { LocalStorageService } from './services/localstorage.service';
import { SwUpdate } from '@angular/service-worker';
import { interval } from 'rxjs';
import { datadogRum } from '@datadog/browser-rum';
import { environment } from '../environments/environment';
import { DataService } from './services/data.service';
import { TrackAuthority } from './models/trackauthority';
import { ProxyUser } from '../app/models/proxyuser';
import { AnalyticsService } from './services/google-analytics.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit {
  appUser = '';
  login = false;
  isServiceWorkerSupported = true;
  lastRoute: string | null = null;
  offline: boolean = !window.navigator.onLine;
  retryCounter: number;
  trackAuthorityModal: TrackAuthority;
  showSharedAuthorityModal: boolean;
  sharedAuthorityNotification: any;

  constructor(
    private _router: Router,
    private pollService: PollService,
    private location: Location,
    private eventBus: EventBusService,
    private user: User,
    private authService: AuthenticationService,
    private localStorage: LocalStorageService,
    private systemStatusService: SystemStatusService,
    private applicationRef: ApplicationRef,
    private dataService: DataService,
    private proxy: ProxyUser,
    private analytics: AnalyticsService
  ) {
    _router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(e => {
      let path = this.location.path();
      this.lastRoute = path;
    });

    this.eventBus.getEventBus(EventType.OKTA_LOGIN_SUCCESS).subscribe(eventData => this.authService.isAuthenticated);

    // seed in case the user was already populated before this subscription was registered
    this.setDatadogUser(this.user);
    this.authService.onUserUpdated.subscribe((user: User) => this.setDatadogUser(user));
  }

  private setDatadogUser(user: User) {
    if (environment.appConfig.rum && datadogRum && user.racf) {
      datadogRum.setUser({
        id: user.racf,
        name: user.displayName || undefined,
        email: user.emails ? user.emails[0] : 'Not Found'
      });
    }
  }

  offlineProcessing() {
    if (this.localStorage.getUserDetail && localStorage.getItem('user')) {
      this.login = true;
      this._router.navigate(['/verify']);
      return;
    } else {
      this.login = true;
      this._router.navigate(['/welcome']);
      return;
    }
  }

  onlineProcessing() {
    if (!this.authService.isAuthenticated && !(this.lastRoute === '/welcome' || this.lastRoute === '/logout')) {
      this.authService.login();
    } else {
      if (this.user.racf != null) {
        localStorage.setItem('user', JSON.stringify(this.user));
      }
      this.fetchUserDetails();
      if (environment.production) {
        this.offlineNotification();
      }
      this.pollService.startPoll(30000);
      this.login = true;
      this._router.navigate(['/authorities'], { queryParams: { status: 'open' } });
    }
  }

  fetchUserDetails() {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user != null) {
      this.appUser = this.user.lastName + ' ' + this.user.firstName + '(' + this.user.racf + ')';
    }
  }
  ngOnInit() {
    window.addEventListener('online', this.onNetworkStatusChange.bind(this));
    window.addEventListener('offline', this.onNetworkStatusChange.bind(this));
    this.analytics.init();
    if (this.offline) {
      this.offlineProcessing();
    } else {
      this.onlineProcessing();
    }

    this.dataService.sharedAuthorityModalStatus.subscribe(response => {
      if (response.status) {
        let authority = new TrackAuthority(response.item.authority, (this.proxy.racf || this.user.racf));
        this.trackAuthorityModal = authority;
        this.showSharedAuthorityModal = response.status;
        this.sharedAuthorityNotification = response;
      } else {
        this.showSharedAuthorityModal = false;
      }
    });
  }

  offlineNotification() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registered => {
        if (registered && Array.isArray(registered) && registered.length === 0) {
          // show offfline in progress message
          this.eventBus.fireEvent(
            EventType.NOTIFY_PENDING_OFFLINE,
            new ATMEventData(null, () => 'System is building Offline Session, it may take few minutes.')
          );
          navigator.serviceWorker.ready
            .then(registration => {
              const self = this;
              this.retryCounter = 50;
              async function getConfig(selfRef) {
                const response = await fetch('/ngsw.json?v=' + new Date().getTime());
                const myJson = await response.json();
                let urlsToCache = [];
                if (myJson.assetGroups[0].urls.length > 0) {
                  urlsToCache = urlsToCache.concat(myJson.assetGroups[0].urls);
                }
                if (urlsToCache.length > 0) {
                  selfRef.validateCaching(urlsToCache, selfRef);
                }
              }
              getConfig(self);
            })
            .catch((err: Error) => {
              console.error(err);
              this.eventBus.fireEvent(
                EventType.NOTIFY_FAILURE_OFFLINE,
                new ATMEventData(null, () => 'Failed to prepare for offline session. Please try reloading the application.')
              );
            });
        }
      });
    } else {
      this.isServiceWorkerSupported = false;
    }
  }

  validateCaching(urlList, thisRef) {
    let promiseArr = [];
    urlList.forEach(url => {
      promiseArr.push(caches.match(url));
    });
    let unResolvedUrlListArr = [];
    Promise.all(promiseArr).then(
      responseArr => {
        responseArr.forEach((response, index) => {
          if (!response) {
            fetch(urlList[index]);
            unResolvedUrlListArr.push(urlList[index]);
          }
        });
        if (unResolvedUrlListArr.length > 0 && this.retryCounter > 0) {
          thisRef.retryCounter--;
          setTimeout(() => {
            thisRef.validateCaching(unResolvedUrlListArr, thisRef);
          }, 7 * 1000);
        } else if (unResolvedUrlListArr.length > 0 && thisRef.retryCounter === 0) {
          thisRef.eventBus.fireEvent(
            EventType.NOTIFY_FAILURE_OFFLINE,
            new ATMEventData(null, () => 'Failed to prepare for offline session. Please try reloading the application.')
          );
        } else {
          thisRef.eventBus.fireEvent(
            EventType.NOTIFY_SUCCESS_OFFLINE,
            new ATMEventData(null, () => 'Offline Session successfully created!')
          );
        }
      },
      function (response) {
        thisRef.validateCaching(urlList, thisRef);
      }
    );
  }

  onNetworkStatusChange() {
    if (window.navigator.onLine) {
      if (!(this.lastRoute == '/welcome' || this.lastRoute == '/logout')) {
        this.authService.login();
      }
      this.offline = false;
      this.pollService.startPoll(30000);
      if (this.authService.isAuthenticated) {
        return this._router.navigate(['/authorities'], { queryParams: { status: 'open' } });
      }
    } else {
      this.offline = true;
      this.pollService.stopPoll();
    }
  }
}
